import { useEffect, useState } from 'react';
import { fetchGlobalStats, type GlobalStats } from '../api/stats';

const OPERATOR_COLOR: Record<string, string> = {
  ORANGE: '#f97316',
  SFR: '#dc2626',
  'BOUYGUES TELECOM': '#3b82f6',
  'FREE MOBILE': '#a855f7',
};
const GEN_COLOR: Record<string, string> = {
  '5G NSA': '#22c55e',
  '5G SA':  '#10b981',
  LTE:      '#3b82f6',
  '4G':     '#3b82f6',
  UMTS:     '#a855f7',
  '3G':     '#a855f7',
  GSM:      '#f59e0b',
  '2G':     '#f59e0b',
};

function pct(n: number, total: number): string {
  if (total === 0) return '0%';
  return `${((n / total) * 100).toFixed(1)}%`;
}

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
      <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">{label}</div>
      <div className="text-2xl font-bold text-slate-100">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function Bar({ label, count, total, color }: { label: string; count: number; total: number; color: string }) {
  const w = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="flex items-center gap-3 text-xs">
      <div className="w-32 text-slate-400 truncate" title={label}>{label}</div>
      <div className="flex-1 h-3 bg-slate-800 rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${w}%`, background: color }} />
      </div>
      <div className="w-24 text-right text-slate-300 font-mono tabular-nums">{count.toLocaleString()} <span className="text-slate-500">({pct(count, total)})</span></div>
    </div>
  );
}

export default function StatsPage() {
  const [stats, setStats] = useState<GlobalStats | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    fetchGlobalStats().then(setStats).catch((e) => setErr(String(e)));
  }, []);

  if (err)   return <div className="p-6 text-rose-400">Error: {err}</div>;
  if (!stats) return <div className="p-6 text-slate-400">Loading…</div>;

  const opTotal = stats.operatorCounts.reduce((s, x) => s + x.count, 0);
  const genTotal = stats.generationCounts.reduce((s, x) => s + x.count, 0);
  const mosTotal = stats.mosBuckets.reduce((s, x) => s + x.count, 0);
  const rsrpTotal = stats.rsrpBuckets.reduce((s, x) => s + x.count, 0);

  return (
    <div className="p-6 overflow-y-auto h-[calc(100vh-56px)]">
      <h1 className="text-xl font-bold text-white mb-4">Global stats</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="Measurements" value={stats.totalMeasurements.toLocaleString()} />
        <StatCard label="Sessions" value={stats.totalSessions.toLocaleString()} />
        <StatCard label="Distance" value={`${(stats.totalDistanceM / 1000).toFixed(1)} km`} sub="across all sessions" />
        <StatCard label="Avg MOS" value={stats.avgMos != null ? stats.avgMos.toFixed(2) : '—'} sub="voice quality" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <StatCard label="Avg download" value={stats.avgDownloadMbps != null ? `${stats.avgDownloadMbps.toFixed(1)} Mbps` : '—'} />
        <StatCard label="Avg upload"   value={stats.avgUploadMbps   != null ? `${stats.avgUploadMbps.toFixed(1)} Mbps` : '—'} />
        <StatCard label="Avg latency"  value={stats.avgLatencyMs    != null ? `${stats.avgLatencyMs.toFixed(0)} ms`    : '—'} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <section className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
          <h2 className="text-sm font-bold text-slate-200 mb-3">Operators</h2>
          <div className="space-y-2">
            {stats.operatorCounts.map((o) => (
              <Bar key={o.operator} label={o.operator} count={o.count} total={opTotal} color={OPERATOR_COLOR[o.operator] ?? '#94a3b8'} />
            ))}
          </div>
        </section>
        <section className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
          <h2 className="text-sm font-bold text-slate-200 mb-3">Generations</h2>
          <div className="space-y-2">
            {stats.generationCounts.map((g) => (
              <Bar key={g.generation} label={g.generation} count={g.count} total={genTotal} color={GEN_COLOR[g.generation] ?? '#94a3b8'} />
            ))}
          </div>
        </section>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <section className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
          <h2 className="text-sm font-bold text-slate-200 mb-3">RSRP buckets</h2>
          <div className="space-y-2">
            {stats.rsrpBuckets.map((b, i) => {
              const colors = ['#22c55e', '#84cc16', '#facc15', '#f97316', '#ef4444', '#7f1d1d'];
              return <Bar key={b.bucket} label={b.bucket} count={b.count} total={rsrpTotal} color={colors[i]} />;
            })}
          </div>
        </section>
        <section className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
          <h2 className="text-sm font-bold text-slate-200 mb-3">MOS distribution</h2>
          <div className="space-y-2">
            {stats.mosBuckets.map((b, i) => {
              const colors = ['#ef4444', '#facc15', '#84cc16', '#22c55e'];
              return <Bar key={b.bucket} label={b.bucket} count={b.count} total={mosTotal} color={colors[i]} />;
            })}
          </div>
        </section>
      </div>

      <section className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
        <h2 className="text-sm font-bold text-slate-200 mb-3">Top cells by sightings</h2>
        <table className="w-full text-xs">
          <thead className="text-slate-500 uppercase tracking-wide">
            <tr>
              <th className="text-left px-2 py-2">CI</th>
              <th className="text-left px-2 py-2">PCI</th>
              <th className="text-left px-2 py-2">TAC</th>
              <th className="text-left px-2 py-2">Operator</th>
              <th className="text-left px-2 py-2">Gen</th>
              <th className="text-right px-2 py-2">Sightings</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/50 font-mono">
            {stats.topCells.map((c, i) => (
              <tr key={i} className="text-slate-300">
                <td className="px-2 py-2">{c.ci ?? '—'}</td>
                <td className="px-2 py-2">{c.pci ?? '—'}</td>
                <td className="px-2 py-2">{c.tac ?? '—'}</td>
                <td className="px-2 py-2">{c.operator ?? '—'}</td>
                <td className="px-2 py-2">{c.networkGeneration ?? '—'}</td>
                <td className="px-2 py-2 text-right">{c.sightings.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
